import React, { useEffect, useState, useRef } from "react";
import {
  LayoutDashboard,
  FolderKanban,
  ChevronDown,
  BarChart3,
  Settings,
  HelpCircle,
  ExternalLink,
  Search,
  FilePlus2,
  ListChecks,
  FileText,
  ShoppingCart,
  ClipboardList,
  FileCheck,
  FileBarChart,
  Menu,
  X,
  Users,
  LogOut,
  Shield,
  Activity,
  User,
  Mail,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  // ✅ New meaningful icons
  Package,
  PackageCheck,
  Fuel,
  Wrench,
  Recycle,
  TrendingUp,
  BookOpen,
  QrCode,
  UserCog,
  MapPin,
  Share2,
  Boxes,
  ClipboardCheck,
  ReceiptText,
  History,
  GitMerge,
  BarChart2,
  Send,
  Truck,
  Factory,
  FlaskConical,
  ShoppingBag,
  Receipt,
  Landmark,
  ScrollText,
  DatabaseZap,
  Layers,
  UserCheck,
} from "lucide-react";
import API_BASE_URL from "../config";
import logo from "../Image/logo1.png";

const SYSTEM_ADMIN_ROLES = ["system admin", "systemadmin", "system administrator", "admin"];

export default function Sidebar({ currentPage, setCurrentPage, sidebarCollapsed, setSidebarCollapsed, onLogout }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [allowedPositionNames, setAllowedPositionNames] = useState([]);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [websiteDetails, setWebsiteDetails] = useState({
    websiteName: "PRPO",
    websiteSlogan: "Management System",
    logoUrl: null
  });

  useEffect(() => {
    const fetchWebsiteDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}api/Website_Details`);
        if (!response.ok) throw new Error("Failed to fetch website details");
        const data = await response.json();
        if (data && data.length > 0) {
          const details = data[0];
          setWebsiteDetails({
            websiteName: details.websiteName || "PRPO",
            websiteSlogan: details.websiteSlogan || "Management System",
            logoUrl: details.hasLogo ? `${API_BASE_URL}api/Website_Details/logo/${details.id}` : null
          });
        }
      } catch (error) {
        console.error("❌ Failed to fetch website details:", error);
      }
    };
    fetchWebsiteDetails();
  }, []);

  const [sidebarPosition, setSidebarPosition] = useState(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const savedPosition = localStorage.getItem('sidebarPosition');
    if (isMobile && (savedPosition === 'left' || savedPosition === 'right')) return 'top';
    return savedPosition || (isMobile ? 'top' : 'left');
  });

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && (sidebarPosition === 'left' || sidebarPosition === 'right')) {
        setSidebarPosition('top');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarPosition]);

  const [showPositionMenu, setShowPositionMenu] = useState(false);
  const [showHorizontalMenu, setShowHorizontalMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const positionMenuRef = useRef(null);
  const horizontalMenuRef = useRef(null);

  const toggleMenu = (menu) => setOpenMenu(openMenu === menu ? null : menu);

  const hasAccess = (featureName) => {
    if (isSystemAdmin) return true;
    if (!allowedPositionNames || allowedPositionNames.length === 0) return false;

    const featureToPositionMap = {
      // ── Main ──
      "Dashboard": ["Dashboard"],
      "CombinedDashboard": ["CombinedDashboard", "Dashboard"],
      "Settings": ["Settings"],
      "Support": ["Support"],

      // ── Purchase Request ──
      "Purchase Request": ["Create Purchase Req", "My Requests", "Purchase Request"],
      "Create Purchase Req": ["Create Purchase Req"],
      "My Requests": ["My Requests"],
      "PurchaseRequests": ["My Requests", "PurchaseRequests"],

      // ── Quotation ──
      "Quotation": ["Quotation"],
      "QuotationForm": ["Quotation", "QuotationForm"],
      "QuotationView": ["Quotation", "QuotationView"],

      // ── Purchase Order ──
      "Purchase Order": ["My PO", "MY Purchase Order", "PO Receiving", "Invoice Transmittal", "Purchase Order"],
      "My PO": ["My PO"],
      "MY Purchase Order": ["MY Purchase Order"],
      "PO Receiving": ["PO Receiving"],
      "Invoice Transmittal": ["Invoice Transmittal"],

      // ── Goods Receipt ──
      "GoodsReceipt": ["GoodsReceipt"],
      "GRViewList": ["GoodsReceipt", "GRViewList"],

      // ── Inventory ──
      "InventoryList": ["InventoryList", "Inventory List", "Inventory"],
      "StockAdjustmentForm": ["StockAdjustmentForm", "Stock Adjustment", "Inventory"],
      "StockAdjustmentList": ["StockAdjustmentList", "Stock Adjustment List", "Inventory"],

      // ── Item Request ──
      "ItemRequestForm": ["ItemRequestForm", "Item Request Form"],
      "ItemRequestView": ["ItemRequestView", "Item Request Viewer"],
      "ItemFolderByDate": ["ItemFolderByDate", "Item Folder ByDate"],
      "GoodIssue": ["GoodIssue", "Goods Issue"],
      "GoodIssue": ["GoodIssue", "Goods Issue", "GoodIssue"],

      // ── Fuel ──
      "FuelRequestForm": ["FuelRequestForm", "Fuel Request Form"],
      "ItemRequestList": ["FuelRequestForm", "ItemRequestList"],
      "FuelRequisitionApproval": ["FuelRequisitionApproval", "Fuel Approval"],

      // ── Approval ──
      "Approval": ["Approval"],
      "PR Approval": ["PR Approval"],
      "Quotations Approval": ["Quotations Approval"],
      "PO Approval": ["PO Approval"],
      "PR Approval History": ["PR Approval History"],
      "PO Approval History": ["PO Approval History"],
      "SecondPR_Approval": ["SecondPR_Approval", "2nd PR Approval"],
      "ItemRequestApproval": ["ItemRequestApproval", "IR Approval"],
      "DisposalApproval": ["DisposalApproval", "Disposal Approval"],

      // ── Scrap ──
      "Scrap": ["Scrap"],
      "Disposal_Form": ["Disposal_Form", "Disposal Form", "Scrap"],
      "Motorpool_Scrap": ["Motorpool_Scrap", "Motorpool Scrap", "Scrap"],
      "Production_Scrap": ["Production_Scrap", "Production Scrap", "Scrap"],
      "Scrap_Loding_Form": ["Scrap_Loding_Form", "Scrap Loading Form", "Scrap"],
      "Sales_Order_Scrap": ["Sales_Order_Scrap", "Sales Order Scrap", "Scrap"],
      "Collection_Receipt": ["Collection_Receipt", "Collection Receipt", "Scrap"],

      // ── Marketing ──
      "Marketing": ["Marketing"],
      "FG_Withdrawal_FORM": ["FG_Withdrawal_FORM", "FG_Withdrawal_FORM"],
      "FG_Withdrawal_FORM_LIST": ["FG_Withdrawal_FORM_LIST", "FG_Withdrawal_FORM_LIST"],
      "GR_Marketing_FORM": ["GR_Marketing_FORM", "GR_Marketing_FORM"],
      "IR-LIST": ["IR-LIST", "GR Marketing List", "IR-LIST"],
      "Inventory_Marketing_PAGE": ["Inventory_Marketing_PAGE", "Inventory_Marketing_PAGE"],
      "SO_Marketing_FORM": ["SO_Marketing_FORM", "SO_Marketing_FORM"],
      "SO_Marketing_LIST": ["SO_Marketing_LIST", "SO_Marketing_LIST"],

      // ── Reporting ──
      "Reporting": ["PR Reporting", "PO Reporting", "IR Reports", "Reporting"],
      "PR Reporting": ["PR Reporting"],
      "PO Reporting": ["PO Reporting"],
      "IR Reports": ["IR Reports", "IRReports"],

      // ── Maintenance ──
      "Maintenance": ["Maintenance"],
      "References": ["References"],
      "UserManagement": ["UserManagement", "User Management"],
      "ReferenceCardAccess": ["ReferenceCardAccess", "Reference Card Access"],
      "InventoryDestinationManager": ["InventoryDestinationManager", "Inventory Destination"],
      "InventoryDistribution": ["InventoryDistribution", "Inventory Distribution"],

      // ── Misc ──
      "LPNGenerate": ["LPNGenerate", "QR Generate"],
    };

    const requiredPositions = featureToPositionMap[featureName] || [featureName];
    return requiredPositions.some(reqPos =>
      allowedPositionNames.some(allowedPos =>
        allowedPos != null &&  // ✅ null check
        reqPos != null &&      // ✅ null check
        allowedPos.toLowerCase().trim() === reqPos.toLowerCase().trim()
      )
    );
  };

  const menuItems = [
    { name: "Dashboard", page: "dashboard", category: "main", access: "Dashboard" },
    { name: "Create Purchase Req.", page: "CreatePO", category: "Purchase Request", access: "Create Purchase Req" },
    { name: "My Requests", page: "MyRequestsPage", category: "Purchase Request", access: "My Requests" },
    { name: "Quotation", page: "UnderMaintenancePage", category: "Quotation", access: "Quotation" },
    { name: "My PO", page: "PurchaseOrder", category: "Purchase Order", access: "My PO" },
    { name: "MY Purchase Order", page: "PurchaseOrdersPage", category: "Purchase Order", access: "MY Purchase Order" },
    { name: "PO Receiving", page: "POReceiving", category: "Purchase Order", access: "PO Receiving" },
    { name: "Invoice Transmittal", page: "POInvoiceTransmittal", category: "Purchase Order", access: "Invoice Transmittal" },
    { name: "GoodsReceipt", page: "GoodsReceipt", category: "GoodsReceipt", access: "GoodsReceipt" },
    { name: "PR Approval", page: "PRApproval", category: "Approval", access: "PR Approval" },
    { name: "Quotations Approval", page: "UnderMaintenancePage", category: "Approval", access: "Quotations Approval" },
    { name: "PO Approval", page: "POApproval", category: "Approval", access: "PO Approval" },
    { name: "PR Approval History", page: "PRApprovalHistory", category: "Approval", access: "PR Approval History" },
    { name: "PO Approval History", page: "POApprovalHistory", category: "Approval", access: "PO Approval History" },
    { name: "PR Reporting", page: "PR_Reports", category: "Reporting", access: "PR Reporting" },
    { name: "PO Reporting", page: "PO_Reports", category: "Reporting", access: "PO Reporting" },
    { name: "References", page: "referencess", category: "Maintenance", access: "References" },
    { name: "User Management", page: "UserManagement", category: "Maintenance", access: "UserManagement" },
    { name: "Settings", page: "Settingss", category: "main", access: "Settings" },
    { name: "Support", page: "UnderMaintenancePage", category: "main", access: "Support" },
    { name: "InventoryDestinationManager", page: "InventoryDestinationManager", category: "InventoryDestinationManager", access: "InventoryDestinationManager" },
    { name: "InventoryDistribution", page: "InventoryDistribution", category: "InventoryDistribution", access: "InventoryDistribution" },
    { name: "SecondPR_Approval", page: "SecondPR_Approval", category: "SecondPR_Approval", access: "SecondPR_Approval" },

    { name: "Fuel Monitor", page: "FuelMonitor", category: "FuelMonitor", access: "FuelMonitor" },

  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (!searchQuery) return false;
    const query = searchQuery.toLowerCase();
    return (item.name.toLowerCase().includes(query) || item.category.toLowerCase().includes(query)) && hasAccess(item.access);
  });

  useEffect(() => { localStorage.setItem('sidebarPosition', sidebarPosition); }, [sidebarPosition]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) { setLoading(false); return; }

    const userData = JSON.parse(storedUser);
    setUser(userData);
    const { userId, userLevelId } = userData;

    console.log("=== SIDEBAR LOADING ===");
    console.log("User Data:", userData);
    console.log("User Level ID:", userLevelId);
    console.log("Position ID:", userData.positionId);

    fetch(`${API_BASE_URL}api/Users/${userId}/profile`)
      .then(async (res) => {
        if (!res.ok) return;
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.startsWith("application/json")) return;
        const imageBlob = await res.blob();
        if (!imageBlob || imageBlob.size === 0) return;
        const imageUrl = URL.createObjectURL(imageBlob);
        setUser((prev) => ({ ...prev, profileImage: imageUrl }));
      })
      .catch(() => { });

    if (userLevelId) {
      fetch(`${API_BASE_URL}api/UserLevels/${userLevelId}`)
        .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
        .then((levelData) => {
          const levelName = levelData.userLevelName || "";
          setUser((prev) => ({ ...prev, userLevelName: levelName }));
          const adminCheck = SYSTEM_ADMIN_ROLES.some(r => r.toLowerCase() === levelName.toLowerCase().trim());
          if (adminCheck) { console.log("👑 SYSTEM ADMIN detected"); setIsSystemAdmin(true); }
        })
        .catch((err) => console.error("❌ Failed to fetch user level:", err));
    }

    if (!userLevelId) { setAllowedPositionNames([]); setLoading(false); return; }

    fetch(`${API_BASE_URL}api/Role_Position_Access/by-user-level/${userLevelId}`)
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data) => { setAllowedPositionNames(data.positionNames || []); setLoading(false); })
      .catch((err) => { console.error("❌ Failed to fetch role access:", err); setAllowedPositionNames([]); setLoading(false); });
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setUserMenuOpen(false);
      if (positionMenuRef.current && !positionMenuRef.current.contains(event.target)) setShowPositionMenu(false);
      if (horizontalMenuRef.current && !horizontalMenuRef.current.contains(event.target)) setShowHorizontalMenu(false);
    }
    function handleEsc(event) {
      if (event.key === "Escape") { setUserMenuOpen(false); setShowPositionMenu(false); setShowHorizontalMenu(false); setSearchQuery(""); }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => { document.removeEventListener("mousedown", handleClickOutside); document.removeEventListener("keydown", handleEsc); };
  }, []);

  const isHorizontal = sidebarPosition === 'top' || sidebarPosition === 'bottom';

  const getSidebarStyles = () => {
    const baseStyles = "fixed bg-gradient-to-br from-slate-50 via-white to-blue-50 border-gray-200/80 flex transform transition-all duration-300 shadow-xl z-40";
    if (isHorizontal) {
      return sidebarPosition === 'top'
        ? `${baseStyles} top-0 left-0 right-0 h-16 border-b flex-row translate-y-0`
        : `${baseStyles} bottom-0 left-0 right-0 h-16 border-t flex-row translate-y-0`;
    }
    if (sidebarCollapsed) {
      return sidebarPosition === 'right'
        ? `${baseStyles} top-0 right-0 h-screen w-64 border-l flex-col md:translate-x-full translate-x-full`
        : `${baseStyles} top-0 left-0 h-screen w-64 border-r flex-col md:-translate-x-full -translate-x-full`;
    }
    switch (sidebarPosition) {
      case 'left': return `${baseStyles} top-0 left-0 h-screen w-64 border-r flex-col ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`;
      case 'right': return `${baseStyles} top-0 right-0 h-screen w-64 border-l flex-col ${sidebarOpen ? "translate-x-0" : "translate-x-full"} md:translate-x-0`;
      default: return `${baseStyles} top-0 left-0 h-screen w-64 border-r flex-col`;
    }
  };

  const getToggleButtonPosition = () => {
    if (sidebarCollapsed) {
      switch (sidebarPosition) {
        case 'left': return 'top-4 left-4';
        case 'right': return 'top-4 right-4';
        default: return 'top-4 right-4';
      }
    }
    switch (sidebarPosition) {
      case 'left': return 'top-4 left-60';
      case 'right': return 'top-4 right-60';
      default: return 'top-4 right-4';
    }
  };

  const getGearButtonPosition = () => {
    if (isHorizontal) return sidebarPosition === 'bottom' ? 'bottom-4 right-16' : 'top-4 right-16';
    return sidebarPosition === 'right' ? 'bottom-20 right-60' : 'bottom-20 left-60';
  };

  const getPositionMenuPlacement = () => {
    switch (sidebarPosition) {
      case 'left': return 'left-16 bottom-0';
      case 'right': return 'right-16 bottom-0';
      case 'top': return 'top-16 right-0';
      case 'bottom': return 'bottom-16 right-0';
      default: return 'left-16 bottom-0';
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 h-screen w-64 bg-gradient-to-br from-slate-50 via-white to-blue-50 border-r border-gray-200/80 flex items-center justify-center z-40">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  // ─── Reusable menu builder ────────────────────────────────────────────────
  const buildMaintenanceItems = (closeFn = () => { }) => [
    hasAccess("References") && {
      icon: <Layers className="w-4 h-4" />,
      label: "References",
      onClick: () => { setCurrentPage && setCurrentPage("referencess"); closeFn(); },
      active: currentPage === "referencess",
    },
    hasAccess("UserManagement") && {
      icon: <UserCog className="w-4 h-4" />,
      label: "User Management",
      onClick: () => { setCurrentPage && setCurrentPage("UserManagement"); closeFn(); },
      active: currentPage === "UserManagement",
    },
    hasAccess("ReferenceCardAccess") && {
      icon: <Shield className="w-4 h-4" />,
      label: "Reference Card Access",
      onClick: () => { setCurrentPage && setCurrentPage("ReferenceCardAccess"); closeFn(); },
      active: currentPage === "ReferenceCardAccess",
    },
    hasAccess("InventoryDestinationManager") && {
      icon: <MapPin className="w-4 h-4" />,
      label: "Inventory Destination",
      onClick: () => { setCurrentPage && setCurrentPage("InventoryDestinationManager"); closeFn(); },
      active: currentPage === "InventoryDestinationManager",
    },
    hasAccess("InventoryDistribution") && {
      icon: <Share2 className="w-4 h-4" />,
      label: "Inventory Distribution",
      onClick: () => { setCurrentPage && setCurrentPage("InventoryDistribution"); closeFn(); },
      active: currentPage === "InventoryDistribution",
    },
  ].filter(Boolean);

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin-slow { animation: spin-slow 3s linear infinite; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: linear-gradient(to bottom, #818cf8, #3b82f6); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: linear-gradient(to bottom, #6366f1, #2563eb); }
      `}</style>

      <div className="flex">
        {sidebarOpen && !isHorizontal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300" onClick={() => setSidebarOpen(false)} />
        )}
        {!isHorizontal && (
          <button
            className={`p-3 md:hidden fixed z-50 bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-xl rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 ${sidebarPosition === 'right' ? 'top-3 right-3' : 'top-3 left-3'}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}
        {!isHorizontal && (
          <button
            className={`hidden md:flex items-center justify-center fixed ${getToggleButtonPosition()} z-50 bg-gradient-to-r from-indigo-600 to-blue-600 text-white w-10 h-10 rounded-full shadow-xl border border-white hover:shadow-2xl hover:scale-110 transition-all duration-300`}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        )}

        <div className={getSidebarStyles()}>
          {isHorizontal ? (
            <>
              {/* Logo */}
              <div className="px-4 py-2 border-r border-gray-200/80 bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg overflow-hidden shadow-lg ring-2 ring-white/30 bg-white flex-shrink-0">
                  <img src={websiteDetails.logoUrl || logo} alt="Logo" className="w-full h-full object-cover" />
                </div>
                <div className="text-white hidden lg:block">
                  <h1 className="font-bold text-lg">{websiteDetails.websiteName}</h1>
                  <p className="text-xs text-blue-100">{websiteDetails.websiteSlogan}</p>
                </div>
              </div>

              {/* Horizontal Menu Button */}
              <div className="px-3 flex items-center relative" ref={horizontalMenuRef}>
                <button
                  onClick={() => setShowHorizontalMenu(!showHorizontalMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-gray-700 hover:text-indigo-600"
                >
                  <Menu className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Menu</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showHorizontalMenu ? 'rotate-180' : ''}`} />
                </button>

                {showHorizontalMenu && (
                  <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} left-0 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[80vh] overflow-y-auto custom-scrollbar animate-fadeIn z-50`}>
                    <div className="p-2 space-y-1">
                      {hasAccess("Dashboard") && (
                        <NavItem icon={<LayoutDashboard className="w-4 h-4" />} label="Dashboard"
                          onClick={() => { setCurrentPage && setCurrentPage("dashboard"); setShowHorizontalMenu(false); }}
                          active={currentPage === "dashboard"} />
                      )}
                      {hasAccess("Dashboard") && (
                        <NavItem icon={<BarChart2 className="w-4 h-4" />} label="Combined Dashboard"
                          onClick={() => { setCurrentPage && setCurrentPage("CombinedDashboard"); setShowHorizontalMenu(false); }}
                          active={currentPage === "CombinedDashboard"} />
                      )}
                      {hasAccess("Purchase Request") && (
                        <Dropdown label="Purchase Request" icon={<FolderKanban className="w-4 h-4" />}
                          open={openMenu === "pr"} onClick={() => toggleMenu("pr")}
                          items={[
                            hasAccess("Create Purchase Req") && { icon: <FilePlus2 className="w-4 h-4" />, label: "Create Purchase Req.", onClick: () => { setCurrentPage && setCurrentPage("CreatePO"); setShowHorizontalMenu(false); }, active: currentPage === "CreatePO" },
                            hasAccess("My Requests") && { icon: <ListChecks className="w-4 h-4" />, label: "My Requests", onClick: () => { setCurrentPage && setCurrentPage("MyRequestsPage"); setShowHorizontalMenu(false); }, active: currentPage === "MyRequestsPage" },
                          ].filter(Boolean)} />
                      )}
                      {hasAccess("Quotation") && (
                        <Dropdown label="Quotation" icon={<ReceiptText className="w-4 h-4" />}
                          open={openMenu === "Quotation"} onClick={() => toggleMenu("Quotation")}
                          items={[
                            hasAccess("QuotationForm") && { icon: <FileText className="w-4 h-4" />, label: "Quotation Form", onClick: () => { setCurrentPage && setCurrentPage("Quotation"); setShowHorizontalMenu(false); }, active: currentPage === "Quotation" },
                            hasAccess("QuotationView") && { icon: <ListChecks className="w-4 h-4" />, label: "Quotation List", onClick: () => { setCurrentPage && setCurrentPage("QuotationView"); setShowHorizontalMenu(false); }, active: currentPage === "QuotationView" },
                          ].filter(Boolean)} />
                      )}
                      {hasAccess("Purchase Order") && (
                        <Dropdown label="Purchase Order" icon={<ShoppingCart className="w-4 h-4" />}
                          open={openMenu === "po"} onClick={() => toggleMenu("po")}
                          items={[
                            hasAccess("My PO") && { icon: <FileCheck className="w-4 h-4" />, label: "Purchase Order", onClick: () => { setCurrentPage && setCurrentPage("PurchaseOrder"); setShowHorizontalMenu(false); }, active: currentPage === "PurchaseOrder" },
                            hasAccess("MY Purchase Order") && { icon: <ClipboardList className="w-4 h-4" />, label: "MY Purchase Order", onClick: () => { setCurrentPage && setCurrentPage("PurchaseOrdersPage"); setShowHorizontalMenu(false); }, active: currentPage === "PurchaseOrdersPage" },
                            hasAccess("PO Receiving") && { icon: <PackageCheck className="w-4 h-4" />, label: "PO Receiving", onClick: () => { setCurrentPage && setCurrentPage("POReceiving"); setShowHorizontalMenu(false); }, active: currentPage === "POReceiving" },
                            hasAccess("Invoice Transmittal") && { icon: <Send className="w-4 h-4" />, label: "Invoice Transmittal", onClick: () => { setCurrentPage && setCurrentPage("POInvoiceTransmittal"); setShowHorizontalMenu(false); }, active: currentPage === "POInvoiceTransmittal" },
                          ].filter(Boolean)} />
                      )}
                      {hasAccess("GoodsReceipt") && (
                        <NavItem icon={<Package className="w-5 h-5" />} label="Goods Receipt (GR)"
                          onClick={() => { setCurrentPage && setCurrentPage("GoodsReceipt"); setShowHorizontalMenu(false); }}
                          active={currentPage === "GoodsReceipt"} />
                      )}
                      {hasAccess("Approval") && (
                        <Dropdown label="Approval" icon={<ClipboardCheck className="w-5 h-5" />}
                          open={openMenu === "approval"} onClick={() => toggleMenu("approval")}
                          items={[
                            hasAccess("PR Approval") && { icon: <FileCheck className="w-4 h-4" />, label: "PR Approval", onClick: () => setCurrentPage && setCurrentPage("PRApproval"), active: currentPage === "PRApproval" },
                            hasAccess("Quotations Approval") && { icon: <ReceiptText className="w-4 h-4" />, label: "Quotations Approval", onClick: () => setCurrentPage && setCurrentPage("UnderMaintenancePage"), active: currentPage === "UnderMaintenancePage" },
                            hasAccess("PO Approval") && { icon: <ShoppingBag className="w-4 h-4" />, label: "PO Approval", onClick: () => setCurrentPage && setCurrentPage("POApproval"), active: currentPage === "POApproval" },
                            hasAccess("PR Approval History") && { icon: <History className="w-4 h-4" />, label: "PR Approval History", onClick: () => setCurrentPage && setCurrentPage("PRApprovalHistory"), active: currentPage === "PRApprovalHistory" },
                            hasAccess("PO Approval History") && { icon: <History className="w-4 h-4" />, label: "PO Approval History", onClick: () => setCurrentPage && setCurrentPage("POApprovalHistory"), active: currentPage === "POApprovalHistory" },
                            hasAccess("SecondPR_Approval") && { icon: <GitMerge className="w-4 h-4" />, label: "2nd PR Approval", onClick: () => setCurrentPage && setCurrentPage("SecondPR_Approval"), active: currentPage === "SecondPR_Approval" },
                            hasAccess("ItemRequestApproval") && { icon: <ClipboardCheck className="w-4 h-4" />, label: "IR Approval", onClick: () => setCurrentPage && setCurrentPage("ItemRequestApproval"), active: currentPage === "ItemRequestApproval" },
                            hasAccess("FuelRequisitionApproval") && { icon: <Fuel className="w-4 h-4" />, label: "Fuel Approval", onClick: () => setCurrentPage && setCurrentPage("FuelRequisitionApproval"), active: currentPage === "FuelRequisitionApproval" },
                            hasAccess("DisposalApproval") && { icon: <Recycle className="w-4 h-4" />, label: "Disposal Approval", onClick: () => setCurrentPage && setCurrentPage("DisposalApproval"), active: currentPage === "DisposalApproval" },
                          ].filter(Boolean)} />
                      )}
                      {hasAccess("Reporting") && (
                        <Dropdown label="Reporting" icon={<BarChart3 className="w-5 h-5" />}
                          open={openMenu === "reporting"} onClick={() => toggleMenu("reporting")}
                          items={[
                            hasAccess("PR Reporting") && { icon: <FileBarChart className="w-4 h-4" />, label: "PR Reporting", onClick: () => setCurrentPage && setCurrentPage("PR_Reports"), active: currentPage === "PR_Reports" },
                            hasAccess("PO Reporting") && { icon: <FileBarChart className="w-4 h-4" />, label: "PO Reporting", onClick: () => setCurrentPage && setCurrentPage("PO_Reports"), active: currentPage === "PO_Reports" },
                            hasAccess("IR Reports") && { icon: <FileBarChart className="w-4 h-4" />, label: "IR Reports", onClick: () => setCurrentPage && setCurrentPage("IRReports"), active: currentPage === "IRReports" },
                          ].filter(Boolean)} />
                      )}
                      {hasAccess("Maintenance") && (
                        <Dropdown label="Maintenance" icon={<Wrench className="w-5 h-5" />}
                          open={openMenu === "maintenance"} onClick={() => toggleMenu("maintenance")}
                          items={[
                            hasAccess("References") && { icon: <Layers className="w-4 h-4" />, label: "References", onClick: () => setCurrentPage && setCurrentPage("referencess"), active: currentPage === "referencess" },
                            hasAccess("UserManagement") && { icon: <UserCog className="w-4 h-4" />, label: "User Management", onClick: () => setCurrentPage && setCurrentPage("UserManagement"), active: currentPage === "UserManagement" },
                            hasAccess("ReferenceCardAccess") && { icon: <Shield className="w-4 h-4" />, label: "Reference Card Access", onClick: () => setCurrentPage && setCurrentPage("ReferenceCardAccess"), active: currentPage === "ReferenceCardAccess" },
                            hasAccess("InventoryDestinationManager") && { icon: <MapPin className="w-4 h-4" />, label: "Inventory Destination", onClick: () => setCurrentPage && setCurrentPage("InventoryDestinationManager"), active: currentPage === "InventoryDestinationManager" },
                            hasAccess("InventoryDistribution") && { icon: <Share2 className="w-4 h-4" />, label: "Inventory Distribution", onClick: () => setCurrentPage && setCurrentPage("InventoryDistribution"), active: currentPage === "InventoryDistribution" },
                          ].filter(Boolean)} />
                      )}
                      {hasAccess("Settings") && (
                        <NavItem icon={<Settings className="w-4 h-4" />} label="Settings"
                          onClick={() => { setCurrentPage && setCurrentPage("Settingss"); setShowHorizontalMenu(false); }}
                          active={currentPage === "Settingss"} />
                      )}
                      {hasAccess("Support") && (
                        <NavItem icon={<HelpCircle className="w-4 h-4" />} label="Support"
                          onClick={() => { setCurrentPage && setCurrentPage("UnderMaintenancePage"); setShowHorizontalMenu(false); }}
                          active={currentPage === "UnderMaintenancePages"} />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Search */}
              <div className="px-3 flex items-center flex-1 max-w-md relative">
                <div className="relative w-full group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  <input type="text" placeholder="Search menu..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm hover:shadow-md" />
                  {searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
                  {searchQuery && filteredMenuItems.length > 0 && (
                    <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto custom-scrollbar z-50 animate-fadeIn`}>
                      <div className="p-2 space-y-1">
                        {filteredMenuItems.map((item, index) => (
                          <button key={index} onClick={() => { setCurrentPage && setCurrentPage(item.page); setSearchQuery(""); }}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-left ${currentPage === item.page ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}>
                            <div><div className="text-sm font-medium">{item.name}</div><div className="text-xs text-gray-500">{item.category}</div></div>
                            <ChevronDown className="w-4 h-4 -rotate-90" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {searchQuery && filteredMenuItems.length === 0 && (
                    <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50 animate-fadeIn`}>
                      <p className="text-sm text-gray-500 text-center">No results found</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1"></div>

              {/* Horizontal User Profile */}
              {user && (
                <div className="px-3 border-l border-gray-200/80 flex items-center relative">
                  <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                    <img src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`} alt="Profile" className="w-8 h-8 rounded-full ring-2 ring-white shadow-md" />
                    <span className="text-sm font-medium text-gray-700 hidden md:inline">{user.firstName}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 hidden md:inline transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {userMenuOpen && (
                    <div ref={dropdownRef} className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} right-0 w-72 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-50 animate-slideUp`}>
                      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-4 text-white">
                        <div className="flex items-center gap-3">
                          <img src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`} alt="Profile" className="w-14 h-14 rounded-full ring-2 ring-white/30 shadow-lg" />
                          <div>
                            <p className="font-bold text-base">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-blue-100">@{user.username || "user"}</p>
                            {isSystemAdmin && <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-yellow-400/20 text-yellow-200 text-xs font-semibold rounded-full border border-yellow-300/30">👑 System Admin</span>}
                          </div>
                        </div>
                      </div>
                      <div className="p-2 space-y-1">
                        <button onClick={() => { setCurrentPage && setCurrentPage("MyProfile"); setUserMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg transition-colors group">
                          <User className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" /><span className="font-medium">My Profile</span>
                        </button>
                        <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2"><Mail className="w-4 h-4 text-gray-400" /><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</span></div>
                          <p className="text-sm font-medium text-gray-800 ml-7">{user.email}</p>
                        </div>
                        <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2"><Shield className="w-4 h-4 text-gray-400" /><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</span></div>
                          <p className="text-sm font-medium text-gray-800 ml-7">{user.positionId || user.userLevelName || "Loading..."}</p>
                        </div>
                        <div className="px-3 py-2.5 text-sm text-gray-600 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-3"><Activity className="w-4 h-4 text-green-500" /><span className="text-xs font-semibold text-green-600 uppercase tracking-wide">Status</span>
                            <div className="ml-auto flex items-center gap-2"><div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div><span className="text-sm font-medium text-green-700">Online</span></div>
                          </div>
                        </div>
                      </div>
                      <div className="p-2 border-t border-gray-200">
                        <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all font-semibold group">
                          <LogOut className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /><span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* VERTICAL LAYOUT */}
              <div className="p-6 border-b border-gray-200/80 bg-gradient-to-r from-indigo-600 to-blue-600">
                <div className="flex items-center gap-3">
                  <div className="w-13 h-12 rounded-xl overflow-hidden shadow-lg ring-2 ring-white/30 bg-white">
                    <img src={logo} alt="Logo" className="w-full h-full object-cover" />
                  </div>
                  <div className="text-white">
                    <h1 className="font-bold text-lg">{websiteDetails.websiteName}</h1>
                    <p className="text-xs text-blue-100">{websiteDetails.websiteSlogan}</p>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="p-4">
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  <input type="text" placeholder="Search menu..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm hover:shadow-md" />
                  {searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
                  {searchQuery && filteredMenuItems.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto custom-scrollbar z-50 animate-fadeIn">
                      <div className="p-2 space-y-1">
                        {filteredMenuItems.map((item, index) => (
                          <button key={index} onClick={() => { setCurrentPage && setCurrentPage(item.page); setSearchQuery(""); }}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors text-left ${currentPage === item.page ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}>
                            <div><div className="text-sm font-medium">{item.name}</div><div className="text-xs text-gray-500">{item.category}</div></div>
                            <ChevronDown className="w-4 h-4 -rotate-90" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {searchQuery && filteredMenuItems.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl p-4 z-50 animate-fadeIn">
                      <p className="text-sm text-gray-500 text-center">No results found for "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Nav */}
              <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto custom-scrollbar">
                {hasAccess("Dashboard") && (
                  <NavItem icon={<LayoutDashboard className="w-5 h-5" />} label="Dashboard"
                    onClick={() => setCurrentPage && setCurrentPage("dashboard")} active={currentPage === "dashboard"} />
                )}
                {hasAccess("CombinedDashboard") && (
                  <NavItem icon={<BarChart2 className="w-5 h-5" />} label="Item Dashboard"
                    onClick={() => setCurrentPage && setCurrentPage("CombinedDashboard")} active={currentPage === "CombinedDashboard"} />
                )}
                {hasAccess("Purchase Request") && (
                  <Dropdown label="Purchase Request" icon={<FolderKanban className="w-5 h-5" />}
                    open={openMenu === "pr"} onClick={() => toggleMenu("pr")}
                    items={[
                      hasAccess("Create Purchase Req") && { icon: <FilePlus2 className="w-4 h-4" />, label: "Create Purchase Req.", onClick: () => setCurrentPage && setCurrentPage("CreatePO"), active: currentPage === "CreatePO" },
                      hasAccess("PurchaseRequests") && { icon: <ListChecks className="w-4 h-4" />, label: "My Requests", onClick: () => setCurrentPage && setCurrentPage("PurchaseRequests"), active: currentPage === "PurchaseRequests" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("Quotation") && (
                  <Dropdown label="Quotation" icon={<ReceiptText className="w-5 h-5" />}
                    open={openMenu === "Quotation"} onClick={() => toggleMenu("Quotation")}
                    items={[
                      hasAccess("QuotationForm") && { icon: <FileText className="w-4 h-4" />, label: "Quotation Form", onClick: () => setCurrentPage && setCurrentPage("Quotation"), active: currentPage === "Quotation" },
                      hasAccess("QuotationView") && { icon: <ListChecks className="w-4 h-4" />, label: "Quotation List", onClick: () => setCurrentPage && setCurrentPage("QuotationView"), active: currentPage === "QuotationView" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("Purchase Order") && (
                  <Dropdown label="Purchase Order" icon={<ShoppingCart className="w-5 h-5" />}
                    open={openMenu === "po"} onClick={() => toggleMenu("po")}
                    items={[
                      hasAccess("My PO") && { icon: <FileCheck className="w-4 h-4" />, label: "Purchase Order", onClick: () => setCurrentPage && setCurrentPage("PurchaseOrder"), active: currentPage === "PurchaseOrder" },
                      hasAccess("MY Purchase Order") && { icon: <ClipboardList className="w-4 h-4" />, label: "MY Purchase Order", onClick: () => setCurrentPage && setCurrentPage("PurchaseOrdersPage"), active: currentPage === "PurchaseOrdersPage" },
                      hasAccess("PO Receiving") && { icon: <PackageCheck className="w-4 h-4" />, label: "PO Receiving", onClick: () => setCurrentPage && setCurrentPage("POReceiving"), active: currentPage === "POReceiving" },
                      hasAccess("Invoice Transmittal") && { icon: <Send className="w-4 h-4" />, label: "Invoice Transmittal", onClick: () => setCurrentPage && setCurrentPage("POInvoiceTransmittal"), active: currentPage === "POInvoiceTransmittal" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("GoodsReceipt") && (
                  <Dropdown label="Goods Receipt (GR)" icon={<Package className="w-5 h-5" />}
                    open={openMenu === "GoodsReceipt"} onClick={() => toggleMenu("GoodsReceipt")}
                    items={[
                      hasAccess("GoodsReceipt") && { icon: <PackageCheck className="w-4 h-4" />, label: "Goods Receipt", onClick: () => setCurrentPage && setCurrentPage("GoodsReceipt"), active: currentPage === "GoodsReceipt" },
                      hasAccess("GRViewList") && { icon: <ListChecks className="w-4 h-4" />, label: "GR View List", onClick: () => setCurrentPage && setCurrentPage("GRViewList"), active: currentPage === "GRViewList" },
                      hasAccess("GRViewList") && { icon: <ScrollText className="w-4 h-4" />, label: "Receiving Reports", onClick: () => setCurrentPage && setCurrentPage("ReceivingReports"), active: currentPage === "ReceivingReports" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("InventoryList") && (
                  <Dropdown label="Inventory" icon={<Boxes className="w-4 h-4" />}
                    open={openMenu === "InventoryList"} onClick={() => toggleMenu("InventoryList")}
                    items={[
                      hasAccess("InventoryList") && { icon: <DatabaseZap className="w-4 h-4" />, label: "Inventory List", onClick: () => { setCurrentPage && setCurrentPage("InventoryList"); setShowHorizontalMenu(false); }, active: currentPage === "InventoryList" },
                      hasAccess("StockAdjustmentForm") && { icon: <FileText className="w-4 h-4" />, label: "Stock Adjustment", onClick: () => { setCurrentPage && setCurrentPage("StockAdjustmentForm"); setShowHorizontalMenu(false); }, active: currentPage === "StockAdjustmentForm" },
                      hasAccess("StockAdjustmentList") && { icon: <ListChecks className="w-4 h-4" />, label: "Stock Adjustment List", onClick: () => { setCurrentPage && setCurrentPage("StockAdjustmentList"); setShowHorizontalMenu(false); }, active: currentPage === "StockAdjustmentList" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("ItemRequestForm") && (
                  <Dropdown label="Item Request Form" icon={<ClipboardList className="w-4 h-4" />}
                    open={openMenu === "ItemRequestForm"} onClick={() => toggleMenu("ItemRequestForm")}
                    items={[
                      hasAccess("ItemRequestForm") && { icon: <FilePlus2 className="w-4 h-4" />, label: "Item Request Form", onClick: () => { setCurrentPage && setCurrentPage("ItemRequestForm"); setShowHorizontalMenu(false); }, active: currentPage === "ItemRequestForm" },
                      hasAccess("ItemRequestView") && { icon: <Search className="w-4 h-4" />, label: "Item Request Viewer", onClick: () => { setCurrentPage && setCurrentPage("ShipToShipRequestViewer"); setShowHorizontalMenu(false); }, active: currentPage === "ShipToShipRequestViewer" },
                      hasAccess("ItemFolderByDate") && { icon: <FolderKanban className="w-4 h-4" />, label: "Item Folder ByDate", onClick: () => { setCurrentPage && setCurrentPage("MRFolderByDate"); setShowHorizontalMenu(false); }, active: currentPage === "MRFolderByDate" },
                      hasAccess("Gatepass") && { icon: <ExternalLink className="w-4 h-4" />, label: "Gatepass", onClick: () => { setCurrentPage && setCurrentPage("Gatepass"); setShowHorizontalMenu(false); }, active: currentPage === "Gatepass" },
                      hasAccess("GoodIssue") && { icon: <Send className="w-4 h-4" />, label: "Goods Issue", onClick: () => { setCurrentPage && setCurrentPage("GoodIssue"); setShowHorizontalMenu(false); }, active: currentPage === "GoodIssue" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("FuelRequestForm") && (
                  <Dropdown label="Fuel Request Form" icon={<Fuel className="w-4 h-4" />}
                    open={openMenu === "FuelRequestForm"} onClick={() => toggleMenu("FuelRequestForm")}
                    items={[
                      hasAccess("FuelRequestForm") && { icon: <FilePlus2 className="w-4 h-4" />, label: "Fuel Request Form", onClick: () => { setCurrentPage && setCurrentPage("FuelRequestForm"); setShowHorizontalMenu(false); }, active: currentPage === "FuelRequestForm" },
                      hasAccess("ItemRequestList") && { icon: <ListChecks className="w-4 h-4" />, label: "Fuel Requisition List", onClick: () => { setCurrentPage && setCurrentPage("FuelRequisitionList"); setShowHorizontalMenu(false); }, active: currentPage === "FuelRequisitionList" },


                      hasAccess("FuelHistory") && { icon: <History className="w-4 h-4" />, label: "Fuel History", onClick: () => { setCurrentPage && setCurrentPage("FuelHistory"); setShowHorizontalMenu(false); }, active: currentPage === "FuelHistory" },
                      hasAccess("FuelMonitor") && { icon: <History className="w-4 h-4" />, label: "Fuel Monitor", onClick: () => { setCurrentPage && setCurrentPage("FuelMonitor"); setShowHorizontalMenu(false); }, active: currentPage === "FuelMonitor" },

                    ].filter(Boolean)} />


                    
                )}
                {hasAccess("Marketing") && (
                  <Dropdown label="Marketing" icon={<TrendingUp className="w-4 h-4" />}
                    open={openMenu === "Marketing"} onClick={() => toggleMenu("Marketing")}
                    items={[
                      hasAccess("FG_Withdrawal_FORM") && { icon: <FilePlus2 className="w-4 h-4" />, label: "FG-FORM", onClick: () => { setCurrentPage && setCurrentPage("FG_Withdrawal_FORM"); setShowHorizontalMenu(false); }, active: currentPage === "FG_Withdrawal_FORM" },
                      hasAccess("FG_Withdrawal_FORM_LIST") && { icon: <ListChecks className="w-4 h-4" />, label: "FG-LIST", onClick: () => { setCurrentPage && setCurrentPage("FG_Withdrawal_FORM_LIST"); setShowHorizontalMenu(false); }, active: currentPage === "FG_Withdrawal_FORM_LIST" },
                      hasAccess("GR_Marketing_FORM") && { icon: <Package className="w-4 h-4" />, label: "GR-FORM", onClick: () => { setCurrentPage && setCurrentPage("GR_Marketing_FORM"); setShowHorizontalMenu(false); }, active: currentPage === "GR_Marketing_FORM" },
                      hasAccess("IR-LIST") && { icon: <ListChecks className="w-4 h-4" />, label: "GR Marketing List", onClick: () => { setCurrentPage && setCurrentPage("GR_Marketing_LIST"); setShowHorizontalMenu(false); }, active: currentPage === "GR_Marketing_LIST" },
                      hasAccess("Inventory_Marketing_PAGE") && { icon: <Boxes className="w-4 h-4" />, label: "Inventory", onClick: () => { setCurrentPage && setCurrentPage("Inventory_Marketing_PAGE"); setShowHorizontalMenu(false); }, active: currentPage === "Inventory_Marketing_PAGE" },
                      hasAccess("SO_Marketing_FORM") && { icon: <ShoppingBag className="w-4 h-4" />, label: "SO-FORM", onClick: () => { setCurrentPage && setCurrentPage("SO_Marketing_FORM"); setShowHorizontalMenu(false); }, active: currentPage === "SO_Marketing_FORM" },
                      hasAccess("SO_Marketing_LIST") && { icon: <ListChecks className="w-4 h-4" />, label: "SO-LIST", onClick: () => { setCurrentPage && setCurrentPage("SO_Marketing_LIST"); setShowHorizontalMenu(false); }, active: currentPage === "SO_Marketing_LIST" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("Approval") && (
                  <Dropdown label="Approval" icon={<ClipboardCheck className="w-5 h-5" />}
                    open={openMenu === "approval"} onClick={() => toggleMenu("approval")}
                    items={[
                      hasAccess("PR Approval") && { icon: <FileCheck className="w-4 h-4" />, label: "PR Approval", onClick: () => setCurrentPage && setCurrentPage("PRApproval"), active: currentPage === "PRApproval" },
                      hasAccess("Quotations Approval") && { icon: <ReceiptText className="w-4 h-4" />, label: "Quotations Approval", onClick: () => setCurrentPage && setCurrentPage("UnderMaintenancePage"), active: currentPage === "UnderMaintenancePage" },
                      hasAccess("PO Approval") && { icon: <ShoppingBag className="w-4 h-4" />, label: "PO Approval", onClick: () => setCurrentPage && setCurrentPage("POApproval"), active: currentPage === "POApproval" },
                      hasAccess("PR Approval History") && { icon: <History className="w-4 h-4" />, label: "PR Approval History", onClick: () => setCurrentPage && setCurrentPage("PRApprovalHistory"), active: currentPage === "PRApprovalHistory" },
                      hasAccess("PO Approval History") && { icon: <History className="w-4 h-4" />, label: "PO Approval History", onClick: () => setCurrentPage && setCurrentPage("POApprovalHistory"), active: currentPage === "POApprovalHistory" },
                      hasAccess("SecondPR_Approval") && { icon: <GitMerge className="w-4 h-4" />, label: "2nd PR Approval", onClick: () => setCurrentPage && setCurrentPage("SecondPR_Approval"), active: currentPage === "SecondPR_Approval" },
                      hasAccess("ItemRequestApproval") && { icon: <ClipboardCheck className="w-4 h-4" />, label: "IR Approval", onClick: () => setCurrentPage && setCurrentPage("ItemRequestApproval"), active: currentPage === "ItemRequestApproval" },
                      hasAccess("FuelRequisitionApproval") && { icon: <Fuel className="w-4 h-4" />, label: "Fuel Approval", onClick: () => setCurrentPage && setCurrentPage("FuelRequisitionApproval"), active: currentPage === "FuelRequisitionApproval" },
                      hasAccess("DisposalApproval") && { icon: <Recycle className="w-4 h-4" />, label: "Disposal Approval", onClick: () => setCurrentPage && setCurrentPage("DisposalApproval"), active: currentPage === "DisposalApproval" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("Reporting") && (
                  <Dropdown label="Reporting" icon={<BarChart3 className="w-5 h-5" />}
                    open={openMenu === "reporting"} onClick={() => toggleMenu("reporting")}
                    items={[
                      hasAccess("PR Reporting") && { icon: <FileBarChart className="w-4 h-4" />, label: "PR Reporting", onClick: () => setCurrentPage && setCurrentPage("PR_Reports"), active: currentPage === "PR_Reports" },
                      hasAccess("PO Reporting") && { icon: <FileBarChart className="w-4 h-4" />, label: "PO Reporting", onClick: () => setCurrentPage && setCurrentPage("PO_Reports"), active: currentPage === "PO_Reports" },
                      hasAccess("IR Reports") && { icon: <FileBarChart className="w-4 h-4" />, label: "IR Reports", onClick: () => setCurrentPage && setCurrentPage("IRReports"), active: currentPage === "IRReports" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("Maintenance") && (
                  <Dropdown label="Maintenance" icon={<Wrench className="w-5 h-5" />}
                    open={openMenu === "maintenance"} onClick={() => toggleMenu("maintenance")}
                    items={[
                      hasAccess("References") && { icon: <Layers className="w-4 h-4" />, label: "References", onClick: () => setCurrentPage && setCurrentPage("referencess"), active: currentPage === "referencess" },
                      hasAccess("UserManagement") && { icon: <UserCog className="w-4 h-4" />, label: "User Management", onClick: () => setCurrentPage && setCurrentPage("UserManagement"), active: currentPage === "UserManagement" },
                      hasAccess("ReferenceCardAccess") && { icon: <Shield className="w-4 h-4" />, label: "Reference Card Access", onClick: () => setCurrentPage && setCurrentPage("ReferenceCardAccess"), active: currentPage === "ReferenceCardAccess" },
                      hasAccess("InventoryDestinationManager") && { icon: <MapPin className="w-4 h-4" />, label: "Inventory Destination", onClick: () => setCurrentPage && setCurrentPage("InventoryDestinationManager"), active: currentPage === "InventoryDestinationManager" },
                      hasAccess("InventoryDistribution") && { icon: <Share2 className="w-4 h-4" />, label: "Inventory Distribution", onClick: () => setCurrentPage && setCurrentPage("InventoryDistribution"), active: currentPage === "InventoryDistribution" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("Scrap") && (
                  <Dropdown label="Scrap" icon={<Recycle className="w-4 h-4" />}
                    open={openMenu === "Scrap"} onClick={() => toggleMenu("Scrap")}
                    items={[
                      hasAccess("Disposal_Form") && { icon: <FilePlus2 className="w-4 h-4" />, label: "Disposal Form", onClick: () => { setCurrentPage && setCurrentPage("Disposal_Form"); setShowHorizontalMenu(false); }, active: currentPage === "Disposal_Form" },
                      hasAccess("Motorpool_Scrap") && { icon: <Truck className="w-4 h-4" />, label: "Motorpool Scrap", onClick: () => { setCurrentPage && setCurrentPage("Motorpool_Scrap"); setShowHorizontalMenu(false); }, active: currentPage === "Motorpool_Scrap" },
                      hasAccess("Production_Scrap") && { icon: <Factory className="w-4 h-4" />, label: "Production Scrap", onClick: () => { setCurrentPage && setCurrentPage("Production_Scrap"); setShowHorizontalMenu(false); }, active: currentPage === "Production_Scrap" },
                      hasAccess("Scrap_Loding_Form") && { icon: <Truck className="w-4 h-4" />, label: "Scrap Loading Form", onClick: () => { setCurrentPage && setCurrentPage("Scrap_Loding_Form"); setShowHorizontalMenu(false); }, active: currentPage === "Scrap_Loding_Form" },
                      hasAccess("Sales_Order_Scrap") && { icon: <ShoppingBag className="w-4 h-4" />, label: "Sales Order", onClick: () => { setCurrentPage && setCurrentPage("Sales_Order_Scrap"); setShowHorizontalMenu(false); }, active: currentPage === "Sales_Order_Scrap" },
                      hasAccess("Collection_Receipt") && { icon: <Receipt className="w-4 h-4" />, label: "Collection Receipt", onClick: () => { setCurrentPage && setCurrentPage("Collection_Receipt"); setShowHorizontalMenu(false); }, active: currentPage === "Collection_Receipt" },
                    ].filter(Boolean)} />
                )}
                {hasAccess("LPNGenerate") && (
                  <NavItem icon={<QrCode className="w-4 h-4" />} label="QR Generate"
                    onClick={() => { setCurrentPage && setCurrentPage("LPNGenerate"); setShowHorizontalMenu(false); }}
                    active={currentPage === "LPNGenerate"} />
                )}
                <div className="relative">
                  {hasAccess("Support") && (
                    <NavItem icon={<HelpCircle className="w-5 h-5" />} label="Support"
                      onClick={() => setCurrentPage && setCurrentPage("UnderMaintenancePage")} active={currentPage === "UnderMaintenancePages"} />
                  )}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-gray-600 font-medium">Online</span>
                  </div>
                </div>
                <NavItem icon={<BookOpen className="w-5 h-5" />} label="User Manual"
                  onClick={() => setCurrentPage && setCurrentPage("PRPOManual")} active={currentPage === "PRPOManual"} />
              </nav>

              {/* Vertical User Profile */}
              <div className="mt-auto p-4 border-t border-gray-200/80 bg-gradient-to-r from-slate-50 to-blue-50 relative">
                {user ? (
                  <div className="relative">
                    <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 group" onClick={() => setUserMenuOpen((prev) => !prev)}>
                      <div className="relative">
                        <img src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`} alt="Profile" className="w-12 h-12 rounded-full ring-2 ring-white shadow-md group-hover:ring-indigo-400 transition-all" />
                        <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>
                      <div className="flex flex-col flex-1 text-left">
                        <span className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{user.firstName} {user.lastName}</span>
                        {isSystemAdmin ? (
                          <span className="text-xs text-amber-600 font-semibold flex items-center gap-1">👑 System Admin</span>
                        ) : (
                          <span className="text-xs text-gray-500 flex items-center gap-1"><div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>Online</span>
                        )}
                      </div>
                      <ChevronDown className={`w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-all duration-300 ${userMenuOpen ? "rotate-180" : ""}`} />
                    </button>

                    {userMenuOpen && (
                      <div ref={dropdownRef} className="absolute bottom-full left-0 mb-3 w-72 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-50 animate-slideUp">
                        <div className="absolute bottom-0 left-6 transform translate-y-1/2 w-4 h-4 bg-white border-b border-r border-gray-200 rotate-45"></div>
                        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-4 text-white">
                          <div className="flex items-center gap-3">
                            <img src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`} alt="Profile" className="w-14 h-14 rounded-full ring-2 ring-white/30 shadow-lg" />
                            <div>
                              <p className="font-bold text-base">{user.firstName} {user.lastName}</p>
                              <p className="text-xs text-blue-100">@{user.username || "user"}</p>
                              {isSystemAdmin && <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-yellow-400/20 text-yellow-200 text-xs font-semibold rounded-full border border-yellow-300/30">👑 System Admin</span>}
                            </div>
                          </div>
                        </div>
                        <div className="p-2 space-y-1">
                          <button onClick={() => { setCurrentPage && setCurrentPage("MyProfile"); setUserMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg transition-colors group">
                            <User className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" /><span className="font-medium">My Profile</span>
                          </button>
                          <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3 mb-2"><Mail className="w-4 h-4 text-gray-400" /><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</span></div>
                            <p className="text-sm font-medium text-gray-800 ml-7">{user.email}</p>
                          </div>
                          <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3 mb-2"><Shield className="w-4 h-4 text-gray-400" /><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</span></div>
                            <p className="text-sm font-medium text-gray-800 ml-7">{user.positionId || "Loading..."}</p>
                          </div>
                          <div className="px-3 py-2.5 text-sm text-gray-600 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-3"><Activity className="w-4 h-4 text-green-500" /><span className="text-xs font-semibold text-green-600 uppercase tracking-wide">Status</span>
                              <div className="ml-auto flex items-center gap-2"><div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div><span className="text-sm font-medium text-green-700">Online</span></div>
                            </div>
                          </div>
                        </div>
                        <div className="p-2 border-t border-gray-200">
                          <button onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all font-semibold group">
                            <LogOut className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /><span>Logout</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-sm text-gray-500 font-medium">Not logged in</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Gear Button - Vertical */}
          {!sidebarCollapsed && !isHorizontal && (
            <div className={`fixed ${getGearButtonPosition()} z-[9999]`}>
              <div className="relative" ref={positionMenuRef}>
                <button onClick={() => setShowPositionMenu(!showPositionMenu)}
                  className="w-9 h-9 bg-gradient-to-r from-gray-600 to-gray-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center">
                  <Settings className="w-5 h-5 animate-spin-slow" />
                </button>
                {showPositionMenu && (
                  <div className={`absolute ${getPositionMenuPlacement()} bg-white rounded-xl shadow-2xl border border-gray-200 p-2 space-y-1 min-w-[160px] z-[10000] animate-fadeIn`}>
                    {[{ pos: 'left', icon: <ArrowLeft className="w-4 h-4" />, label: 'Left', cls: 'hidden md:flex' },
                    { pos: 'right', icon: <ArrowRight className="w-4 h-4" />, label: 'Right', cls: 'hidden md:flex' },
                    { pos: 'top', icon: <ArrowUp className="w-4 h-4" />, label: 'Top', cls: 'flex' },
                    { pos: 'bottom', icon: <ArrowDown className="w-4 h-4" />, label: 'Bottom', cls: 'flex' }].map(({ pos, icon, label, cls }) => (
                      <button key={pos} onClick={() => { setSidebarPosition(pos); setShowPositionMenu(false); }}
                        className={`${cls} w-full items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === pos ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}>
                        {icon}<span className="text-sm font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gear Button - Horizontal */}
          {isHorizontal && (
            <div className="px-3 flex items-center relative" ref={positionMenuRef}>
              <button onClick={() => setShowPositionMenu(!showPositionMenu)}
                className="w-9 h-9 bg-white/50 hover:bg-white/80 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center">
                <Settings className="w-5 h-5 text-gray-600 animate-spin-slow" />
              </button>
              {showPositionMenu && (
                <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} right-0 bg-white rounded-xl shadow-2xl border border-gray-200 p-2 space-y-1 min-w-[160px] z-[10000] animate-fadeIn`}>
                  {[{ pos: 'left', icon: <ArrowLeft className="w-4 h-4" />, label: 'Left' },
                  { pos: 'right', icon: <ArrowRight className="w-4 h-4" />, label: 'Right' },
                  { pos: 'top', icon: <ArrowUp className="w-4 h-4" />, label: 'Top' },
                  { pos: 'bottom', icon: <ArrowDown className="w-4 h-4" />, label: 'Bottom' }].map(({ pos, icon, label }) => (
                    <button key={pos} onClick={() => { setSidebarPosition(pos); setShowPositionMenu(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === pos ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}>
                      {icon}<span className="text-sm font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Dropdown({ label, icon, open, onClick, items }) {
  return (
    <div className="space-y-1">
      <button onClick={onClick}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 ${open ? "bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-600 shadow-sm" : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50"}`}>
        <div className="flex items-center gap-3">
          <span className={`transition-transform ${open ? "text-indigo-600 scale-110" : "text-gray-500"}`}>{icon}</span>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-all duration-300 ${open ? "rotate-180 text-indigo-600" : "text-gray-500"}`} />
      </button>
      {open && items.length > 0 && (
        <div className="ml-6 pl-4 mt-1 space-y-0.5 border-l-2 border-indigo-200 animate-fadeIn">
          {items.map((item, i) => (
            <NavItem key={i} icon={item.icon} label={item.label} onClick={item.onClick} active={item.active} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavItem({ icon, label, external, onClick, active }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl group transition-all duration-200 ${active ? "bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-lg shadow-indigo-500/30 scale-[0.98]" : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:scale-[0.98]"}`}>
      <div className="flex items-center gap-3">
        <span className={`transition-transform group-hover:scale-110 ${active ? "text-white" : "text-gray-500"}`}>{icon}</span>
        <span className="text-sm font-medium whitespace-nowrap">{label}</span>
      </div>
      {external && <ExternalLink className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-all ${active ? "text-white" : "text-gray-400"}`} />}
    </button>
  );
}